import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import {
  EXEC_REPORT_PERIOD,
  getExecReport,
  qk,
  refreshExecReport,
  type ExecReportResponse,
} from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Badge from '../components/Badge.js';
import Card from '../components/Card.js';
import EmptyState, { ErrorState } from '../components/EmptyState.js';
import { LoadingBlock } from '../components/Spinner.js';
import Table from '../components/Table.js';
import { useActivePid } from '../components/Layout.js';
import { formatDateTime, formatMonthKey, formatNumber, formatTL } from '../lib/format.js';

/**
 * §19.3 UZUN VADELİ — 12-24 aylık yönetici raporu.
 *
 * ŞARTNAMENİN TEK KURALI ve bu sayfanın var oluş sebebi: "Dashboard'un 'Uzun Vadeli'
 * ekranı YALNIZCA `exec_reports` + `monthly_agg`'den okur; kullanıcı 18 aylık rapor
 * istediğinde CANLI AĞIR SORGU ÇALIŞMAZ — son hazır rapor gösterilir, 'şu tarihte
 * hazırlandı, yenile' butonu job'ı kuyruğa atar ve UI 'hazırlanıyor' durumunu gösterir."
 *
 * Bu yüzden sayfada hiçbir ClickHouse sorgusu tetikleyen uç YOKTUR: tek okuma
 * `GET /exec-report` (hazır JSON), tek yazma `POST /exec-report/refresh` (202, kuyruk).
 * FinOps'un "ağır sorgular canlı paneli yavaşlatamaz" kararı (§19.3) panelin
 * kendisinde de tutulur — yoksa karar yalnız sunucuda kalır ve panel onu deler.
 *
 * ÜÇ HÂL ve nasıl ayırt edildikleri (§19.3 "hazırlanıyor" durumu buradan doğar):
 *  - `{status:'missing'}`      → hiç hazırlanmamış  → boş durum + "Raporu hazırla"
 *  - payload var, `months` BOŞ → kuyrukta, job koşmadı → "hazırlanıyor"
 *  - payload dolu              → hazır → tablolar + "şu tarihte hazırlandı"
 * İkinci hâl `refresh` ucunun 202'den ÖNCE boş payload yazmasından doğar; panel bunu
 * "veri yok" sanmamalıdır — kullanıcı yenile'ye bastığını bilir, "kayboldu" görmemeli.
 */

/** Rapor hazır mı — `months` dolu olması job'ın gerçekten koştuğunun tek kanıtıdır. */
function isReady(data: ExecReportResponse | undefined): data is Exclude<
  ExecReportResponse,
  { status: 'missing' }
> {
  return data !== undefined && 'payload' in data && data.payload.months.length > 0;
}

/** Kuyrukta mı — payload var ama job henüz doldurmamış. */
function isQueued(data: ExecReportResponse | undefined): boolean {
  return data !== undefined && 'payload' in data && data.payload.months.length === 0;
}

/** YoY değişim rengi: artış yeşil, azalış kırmızı. Ciro ve CR için de doğrudur. */
function changeTone(pct: number): string {
  if (pct > 0) return 'text-emerald-700';
  if (pct < 0) return 'text-red-600';
  return 'text-[#6b7280]';
}

/**
 * İşaretli sayıyı TEK metin düğümü olarak üretir.
 *
 * `{x > 0 ? '+' : ''}{formatNumber(x)}%` yazmak DOM'da üç ayrı düğüm bırakır;
 * `getByText('+35,0%')` böyle bir metni bulamaz ve okuyucu için de tek parça değildir.
 * Biçimlendirmeyi JS'te bitirmek hem testi hem erişilebilirliği düzeltir.
 */
function signed(value: number, digits: number, suffix: string): string {
  return `${value > 0 ? '+' : ''}${formatNumber(value, digits)}${suffix}`;
}

export default function UzunVadeli() {
  const pid = useActivePid();
  const queryClient = useQueryClient();

  const report = useQuery({
    queryKey: qk.execReport(pid, EXEC_REPORT_PERIOD),
    queryFn: ({ signal }) => getExecReport(pid, EXEC_REPORT_PERIOD, signal),
    /*
     * Rapor GECELİK üretilir (§19.1 profil: eco haftalık, balanced/performance gecelik).
     * Sık yenilemek hazır JSON'u boşuna çeker; sayfa açılışında bir kez yeter.
     */
    staleTime: 10 * 60_000,
    retry: false,
  });

  const refresh = useMutation({
    mutationFn: () => refreshExecReport(pid, EXEC_REPORT_PERIOD),
    onSuccess: () => {
      // 202 döndü: uç boş payload yazdı, sayfa "hazırlanıyor"a geçmeli.
      void queryClient.invalidateQueries({ queryKey: qk.execReport(pid, EXEC_REPORT_PERIOD) });
    },
  });

  const data = report.data;
  const hazir = isReady(data);
  const kuyrukta = isQueued(data);
  const payload = hazir ? data.payload : null;

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="uzun-vadeli-sayfasi">
      {/* başlık */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">Uzun Vadeli</h1>
          <BilgiDugmesi testId="uzun-vadeli-bilgi">§19.3 — bu sayfa HAZIR raporu okur: 12-24 aylık trend, YoY, kanal karması ve funnel değişimi gece penceresinde hesaplanır. Rapor istemek canlı ağır sorgu ÇALIŞTIRMAZ; panelin hızı uzun dönem sorgularından etkilenmez.</BilgiDugmesi>
          <span className="ml-1 hidden text-[13px] text-[#9ca3af] md:inline">
            Hazır yönetici raporu · canlı sorgu çalışmaz
          </span>
        </div>

        <div className="flex items-center gap-3">
          {hazir ? (
            <span className="text-[12px] text-[#6b7280]">
              hazırlandı: {formatDateTime(data.computed_at)}
            </span>
          ) : null}
          <button
            type="button"
            data-testid="uzun-vadeli-yenile"
            className="btn-primary"
            /* Kuyruktayken kilitli: ikinci istek aynı satırı tekrar boşaltır ve
               kullanıcı hazır raporu bile kaybederdi. */
            disabled={refresh.isPending || kuyrukta}
            onClick={() => refresh.mutate()}
          >
            <RefreshCw
              aria-hidden="true"
              className={`h-3.5 w-3.5 ${refresh.isPending || kuyrukta ? 'animate-spin' : ''}`}
            />
            {kuyrukta ? 'Hazırlanıyor' : 'Yenile'}
          </button>
        </div>
      </div>

      {/* istatistikler */}
      <div className="flex flex-wrap items-center gap-8 border-b border-[#e5e7eb] bg-white px-6 py-2.5">
        {report.isLoading ? (
          <span className="animate-pulse text-[12px] text-[#9ca3af]">Veri yükleniyor…</span>
        ) : (
          [
            { label: 'Dönem', value: payload?.generatedFor ?? '—' },
            { label: 'Ay', value: payload ? formatNumber(payload.months.length) : '—' },
            { label: 'YoY metrik', value: payload ? formatNumber(payload.yoy.length) : '—' },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-2">
              <span className="text-[12px] text-[#9ca3af]">{stat.label}</span>
              <span className="num text-[13px] font-semibold text-[#111827]">{stat.value}</span>
            </div>
          ))
        )}
      </div>

      {/* içerik */}
      <div className="space-y-6 px-6 py-8">
        {report.isLoading ? <LoadingBlock label="Rapor okunuyor…" /> : null}

        {report.isError ? (
          <ErrorState
            title="Rapor alınamadı"
            description="Uzun vadeli rapor hazır JSON'dan okunur; sunucuya ulaşılamıyor."
            onRetry={() => void report.refetch()}
          />
        ) : null}

        {!report.isLoading && !report.isError && !hazir ? (
          <EmptyState
            title={kuyrukta ? 'Rapor hazırlanıyor' : 'Uzun vadeli rapor henüz hazırlanmadı'}
            description={
              kuyrukta
                ? 'İstek kuyruğa alındı. Hesaplama gece penceresinde koşar (§19.3) — hazır olunca bu sayfa doğrudan gösterir.'
                : 'Bu rapor gece penceresinde toplu olarak hesaplanır. Şimdi hazırlanmasını istersen kuyruğa alabilirsin.'
            }
            actionLabel={kuyrukta ? undefined : 'Raporu hazırla'}
            onAction={kuyrukta ? undefined : () => refresh.mutate()}
          />
        ) : null}

        {payload !== null ? (
          <>
            <Card
              title="Yıldan yıla değişim"
              subtitle="Son 12 ay, önceki 12 aya karşı (§19.3)."
            >
              <Table
                columns={[
                  { key: 'metric', header: 'Metrik' },
                  {
                    key: 'previous',
                    header: 'Önceki',
                    align: 'right',
                    render: (r) => <span className="num">{formatNumber(r.previous, 2)}</span>,
                  },
                  {
                    key: 'current',
                    header: 'Şimdi',
                    align: 'right',
                    render: (r) => <span className="num">{formatNumber(r.current, 2)}</span>,
                  },
                  {
                    key: 'changePct',
                    header: 'Değişim',
                    align: 'right',
                    render: (r) => (
                      <span className={`num font-semibold ${changeTone(r.changePct)}`}>
                        {signed(r.changePct, 1, '%')}
                      </span>
                    ),
                  },
                ]}
                rows={payload.yoy}
                rowKey={(r) => r.metric}
                emptyTitle="YoY karşılaştırması yok"
                emptyDescription="Karşılaştırma için en az 24 ay veri gerekir."
              />
            </Card>

            <Card title="Aylık trend" subtitle="monthly_agg'den okunur; ham veriye inilmez.">
              <Table
                columns={[
                  {
                    key: 'month',
                    header: 'Ay',
                    /*
                      §103.2 — makine anahtarı ('2026-08') KALIR, insan tarihi
                      yanına gelir: export dosyasındaki satırla ekrandaki satır
                      eşleştirilebilir kalmalı (§101.2 KARAR 3).
                    */
                    render: (r) => (
                      <span data-testid="uzun-vadeli-ay">
                        <span className="num">{r.month}</span>
                        <span className="ml-2 text-[12px] text-[#9ca3af]">
                          {formatMonthKey(r.month)}
                        </span>
                      </span>
                    ),
                  },
                  {
                    key: 'sessions',
                    header: 'Oturum',
                    align: 'right',
                    render: (r) => <span className="num">{formatNumber(r.sessions)}</span>,
                  },
                  {
                    key: 'conversions',
                    header: 'Dönüşüm',
                    align: 'right',
                    render: (r) => <span className="num">{formatNumber(r.conversions)}</span>,
                  },
                  {
                    key: 'cr',
                    header: 'CR',
                    align: 'right',
                    render: (r) => <span className="num">%{formatNumber(r.cr * 100, 2)}</span>,
                  },
                  {
                    key: 'revenue',
                    header: 'Ciro',
                    align: 'right',
                    render: (r) => <span className="num">{formatTL(r.revenue)}</span>,
                  },
                ]}
                rows={payload.months}
                rowKey={(r) => r.month}
                stickyHeader
              />
            </Card>

            {/*
              §19.3 — "kanal karması evrimi" raporun saydığı bileşenlerden biridir ve
              payload'da (`channelMix`) ZATEN VARDI; yalnız çizilmiyordu. Pay, aynı ayın
              satırlarından hesaplanır — bu bir metrik TÜRETMESİ değil, verilen sayıların
              oranıdır (§19.3'ün "canlı ağır sorgu yok" kuralı korunur).
            */}
            {payload.channelMix.length > 0 ? (
              <div data-testid="uzun-vadeli-kanal">
                <Card
                  title="Kanal karması evrimi"
                  subtitle="Aylık oturumların kanallara dağılımı; hazır rapordan okunur."
                >
                  <Table
                    columns={[
                      {
                        key: 'month',
                        header: 'Ay',
                        render: (r) => (
                          <span>
                            <span className="num">{r.month}</span>
                            <span className="ml-2 text-[12px] text-[#9ca3af]">
                              {formatMonthKey(r.month)}
                            </span>
                          </span>
                        ),
                      },
                      { key: 'channel', header: 'Kanal' },
                      {
                        key: 'sessions',
                        header: 'Oturum',
                        align: 'right',
                        render: (r) => <span className="num">{formatNumber(r.sessions)}</span>,
                      },
                      {
                        key: 'pay',
                        header: 'Pay',
                        align: 'right',
                        render: (r) => {
                          const ayToplami = payload.channelMix
                            .filter((x) => x.month === r.month)
                            .reduce((acc, x) => acc + x.sessions, 0);
                          return (
                            <span className="num">
                              {ayToplami === 0 ? '—' : `%${formatNumber((r.sessions / ayToplami) * 100, 1)}`}
                            </span>
                          );
                        },
                      },
                    ]}
                    rows={payload.channelMix}
                    rowKey={(r) => `${r.month}-${r.channel}`}
                    stickyHeader
                  />
                </Card>
              </div>
            ) : null}

            <div className="grid gap-6 lg:grid-cols-2">
              <Card title="En iyi sayfalar" subtitle="Dönüşüm oranına göre.">
                <Table
                  columns={[
                    { key: 'url_path', header: 'Yol' },
                    {
                      key: 'sessions',
                      header: 'Oturum',
                      align: 'right',
                      render: (r) => <span className="num">{formatNumber(r.sessions)}</span>,
                    },
                    {
                      key: 'cr',
                      header: 'CR',
                      align: 'right',
                      render: (r) => <span className="num">%{formatNumber(r.cr * 100, 2)}</span>,
                    },
                  ]}
                  rows={payload.bestPages}
                  rowKey={(r) => r.url_path}
                  emptyTitle="Sayfa kırılımı yok"
                />
              </Card>

              <Card title="En kötü sayfalar" subtitle="İlgi görüp dönüştürmeyenler.">
                <Table
                  columns={[
                    { key: 'url_path', header: 'Yol' },
                    {
                      key: 'sessions',
                      header: 'Oturum',
                      align: 'right',
                      render: (r) => <span className="num">{formatNumber(r.sessions)}</span>,
                    },
                    {
                      key: 'cr',
                      header: 'CR',
                      align: 'right',
                      render: (r) => <span className="num">%{formatNumber(r.cr * 100, 2)}</span>,
                    },
                  ]}
                  rows={payload.worstPages}
                  rowKey={(r) => r.url_path}
                  emptyTitle="Sayfa kırılımı yok"
                />
              </Card>
            </div>

            {payload.funnelShift.length > 0 ? (
              <Card
                title="Funnel değişimi"
                subtitle="Adım bazında kayıp oranı: şimdi ve önceki dönem."
                actions={<Badge tone="neutral">{payload.funnelShift.length} adım</Badge>}
              >
                <Table
                  columns={[
                    { key: 'step', header: 'Adım' },
                    {
                      key: 'dropPctPrev',
                      header: 'Önceki kayıp',
                      align: 'right',
                      render: (r) => <span className="num">%{formatNumber(r.dropPctPrev, 1)}</span>,
                    },
                    {
                      key: 'dropPctNow',
                      header: 'Şimdiki kayıp',
                      align: 'right',
                      render: (r) => <span className="num">%{formatNumber(r.dropPctNow, 1)}</span>,
                    },
                    {
                      key: 'delta',
                      header: 'Fark',
                      align: 'right',
                      /* Kayıpta ARTIŞ kötüdür — YoY tablosunun tersi; renk buna göre çevrilir. */
                      render: (r) => {
                        const delta = r.dropPctNow - r.dropPctPrev;
                        return (
                          <span className={`num font-semibold ${changeTone(-delta)}`}>
                            {signed(delta, 1, ' puan')}
                          </span>
                        );
                      },
                    },
                  ]}
                  rows={payload.funnelShift}
                  rowKey={(r) => r.step}
                />
              </Card>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
